import React from 'react';
import PropTypes from 'prop-types';
import { LinkElement } from '@els/els-react--link-element';

function SearchTipsModalContent({ onExampleClick = () => {}, withExamplesLinks = true }) {
  const examples = [
    {
      key: '1',
      value: 'How has the development of polydopamine coatings enhanced the adhesion and biocompatibility of medical implants?',
    },
    { key: '2', value: 'What are the key challenges in Mn based catalysts for VOCs removal?' },
    { key: '3', value: 'Development of conductive polymers enhancing applications in flexible electronics' },
  ];
  const substanceExamples = [
    { key: '1', value: 'Substance name', example: 'atenolol' },
    { key: '2', value: 'CAS Registry Number', example: '102625-70-7' },
    { key: '3', value: 'Molecular Formula', example: 'Pt(PPh3)3' },
    { key: '4', value: 'Reactions', example: 'Suzuki coupling' },
    { key: '5', value: 'Substance Effect', example: 'anticoagulant' },
  ];

  return (
    <div className="searchtips-modal u-els-padding">
      <div className="header u-els-padding-bottom-2x">
        <h4>Learn how it works</h4>
      </div>
      <div className="content">
        <h4 className="u-els-font-family-bold">Introducing AI Powered Discovery for Document Search</h4>
        <p className="u-els-padding-bottom u-els-padding-top">
          We&#39;ve enhanced Reaxys search with new generative AI capabilities that work alongside your existing keyword-based workflows.
          These improvements make it easier to explore complex chemical topics, retrieve relevant literature, and understand results faster
          - without removing or replacing any current functionality.
        </p>

        <h4 className="u-els-font-family-bold u-els-padding-bottom">What&#39;s New</h4>

        <h5 className="u-els-padding-bottom">1. Natural Language Search - Search the Way You Think</h5>
        <p className="u-els-padding-bottom">
          Reaxys now understands everyday language, allowing you to express questions naturally from simple queries to complex scientific
          topics.
        </p>
        <p className="u-els-padding-bottom">Instead of relying solely on keywords, you can ask open ended questions such as:</p>
        <ul className="c-els-list c-els-list--no-indent u-els-padding-bottom">
          {examples.map((example) => (
            <li className="c-els-list__item" key={example.key}>
              {withExamplesLinks ? (
                <LinkElement
                  href="#"
                  inline
                  data-e2e={`search-tip-example-${example.key}`}
                  onClick={(e) => {
                    e.preventDefault();
                    onExampleClick(example.value);
                  }}>
                  {example.value}
                </LinkElement>
              ) : (
                `"${example.value}"`
              )}
            </li>
          ))}
        </ul>
        <p className="u-els-padding-bottom">
          This makes content more discoverable across materials science, drug discovery, chemical engineering, polymer science, and other
          disciplines.
        </p>

        <h5 className="u-els-padding-bottom">2. AI Generated Summaries - Fast Insights from Large Literature Sets</h5>
        <p className="u-els-padding-bottom">
          For each natural language query, Reaxys retrieves the most relevant scientific documents - journal articles and patents - selected
          from a trusted corpus of over <strong>125 million documents</strong> and <strong>48 million patents</strong>.
        </p>
        <p className="u-els-padding-bottom">
          Up to 20 of these top ranked results are then synthesized into a concise summary tailored to your question, based only on titles
          and abstracts - not on any LLM internal knowledge.
        </p>
        <p className="u-els-padding-bottom">Every summary includes:</p>
        <ul className="c-els-list c-els-list--no-indent u-els-padding-bottom">
          <li className="c-els-list__item">Traceable links to original sources</li>
          <li className="c-els-list__item">Citations for direct verification</li>
          <li className="c-els-list__item">Context aware insights specific to your query</li>
        </ul>
        <p className="u-els-padding-bottom">This helps you quickly understand a research area and make decisions more efficiently.</p>

        <h4 className="u-els-font-family-bold u-els-padding-bottom">What&#39;s Not Changing: Keyword Search Remains Fully Available</h4>
        <p className="u-els-padding-bottom">
          Your familiar keyword-based search continues to work exactly as before. In fact, it remains the best option for highly specific
          queries such as substance names, CAS numbers, molecular formulas, reaction types, and ADME effects:
        </p>
        <ul className="c-els-list c-els-list--no-indent u-els-padding-bottom">
          {substanceExamples.map((example) => (
            <li className="c-els-list__item" key={example.key}>
              {example.value} (e.g.&nbsp;
              {withExamplesLinks ? (
                <LinkElement
                  href="#"
                  inline
                  data-e2e={`search-tip-example-${example.key}`}
                  onClick={(e) => {
                    e.preventDefault();
                    onExampleClick(example.example);
                  }}>
                  {example.example}
                </LinkElement>
              ) : (
                example.example
              )}
              )
            </li>
          ))}
        </ul>
        <p className="u-els-padding-bottom">
          This release <strong>adds</strong> natural language discovery and AI generated summaries on top of existing capabilities - nothing
          has been removed.
        </p>

        <h4 className="u-els-font-family-bold u-els-padding-bottom">How the System Works</h4>
        <ol className="c-els-list c-els-list--no-indent u-els-padding-bottom">
          <li className="c-els-list__item">You enter a natural language query.</li>
          <li className="c-els-list__item">Reaxys interprets the question and retrieves a context relevant set of documents.</li>
          <li className="c-els-list__item">A real-time summary is generated based on the most relevant results.</li>
          <li className="c-els-list__item">Each summary links directly to its sources, enabling quick validation.</li>
        </ol>
        <p className="u-els-padding-bottom">
          All responses are generated dynamically and may occasionally vary; your scientific judgment remains essential when applying
          insights.
        </p>

        <h4 className="u-els-font-family-bold u-els-padding-bottom">Privacy by Design</h4>
        <p className="u-els-padding-bottom">
          Reaxys does <strong>not</strong> learn from or store user queries. The model is hosted in Microsoft Azure, ensuring your queries
          are not logged or used for model training.
        </p>

        <h4 className="u-els-font-family-bold u-els-padding-bottom">Help Us Improve</h4>
        <p className="u-els-padding-bottom">
          Your feedback guides us. Please rate AI results with thumbs up/down and share suggestions - we&#39;re continually refining the
          system.
        </p>
      </div>
    </div>
  );
}

SearchTipsModalContent.propTypes = {
  onExampleClick: PropTypes.func,
  withExamplesLinks: PropTypes.bool,
};

export default SearchTipsModalContent;
